/**
 * FAQ block: opening and closing answers.
 *
 * Markup: template-parts/blocks/faq.php and template-parts/blocks/faq-item.php.
 * The button state (aria-expanded) is the single source of truth, styles read it too.
 * A collapsed panel has hidden="until-found", so it is out of the tab order and
 * the accessibility tree, and the browser's find in page can still open it.
 */

const ANIMATION_DURATION = 300;
const ANIMATION_EASING = 'ease-in-out';

const reducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' );

/**
 * One FAQ block.
 */
class Faq {

	/**
	 * @param {HTMLElement} element Block root element (.b-faq).
	 */
	constructor( element ) {

		this.element = element;
		this.allowMultiple = element.dataset.mode === 'toggle';
		this.animate = element.dataset.animate === 'true';
		this.animations = new WeakMap();
		this.items = [ ...element.querySelectorAll( '.js-faq-toggle' ) ]
			.map( ( button ) => ( {
				button,
				panel: document.getElementById( button.getAttribute( 'aria-controls' ) ),
			} ) )
			.filter( ( { panel } ) => panel !== null );

		this.items.forEach( ( item ) => this.bind( item ) );

	}

	/**
	 * @param {Object}      item        Item.
	 * @param {HTMLElement} item.button Toggle button.
	 * @param {HTMLElement} item.panel  Answer panel.
	 */
	bind( item ) {

		item.button.addEventListener( 'click', () => {

			this.setOpen( item, ! this.isOpen( item ) );

		} );

		// Find in page reveals the panel itself, only the state has to follow.
		item.panel.addEventListener( 'beforematch', () => {

			this.closeOthers( item );
			item.button.setAttribute( 'aria-expanded', 'true' );

		} );

	}

	/**
	 * @param {Object} item Item.
	 * @return {boolean} Whether the answer is open.
	 */
	isOpen( item ) {

		return item.button.getAttribute( 'aria-expanded' ) === 'true';

	}

	/**
	 * @param {Object} current Item that stays as it is.
	 */
	closeOthers( current ) {

		if ( this.allowMultiple ) {

			return;

		}

		this.items
			.filter( ( item ) => item !== current && this.isOpen( item ) )
			.forEach( ( item ) => this.setOpen( item, false ) );

	}

	/**
	 * @param {Object}  item           Item.
	 * @param {boolean} open           Target state.
	 * @param {boolean} [animate=true] Whether the change may be animated.
	 */
	setOpen( item, open, animate = true ) {

		if ( open ) {

			this.closeOthers( item );

		}

		item.button.setAttribute( 'aria-expanded', open ? 'true' : 'false' );

		const { panel } = item;
		const running = this.animations.get( panel );
		const startHeight = running ? panel.getBoundingClientRect().height : null;

		if ( running ) {

			running.cancel();

		}

		if ( open ) {

			panel.removeAttribute( 'hidden' );

		}

		if ( ! animate || ! this.animate || reducedMotion.matches ) {

			if ( ! open ) {

				panel.setAttribute( 'hidden', 'until-found' );

			}

			return;

		}

		const from = startHeight ?? ( open ? 0 : panel.scrollHeight );
		const to = open ? panel.scrollHeight : 0;

		const animation = panel.animate(
			[
				{ height: `${ from }px`, overflow: 'hidden' },
				{ height: `${ to }px`, overflow: 'hidden' },
			],
			{ duration: ANIMATION_DURATION, easing: ANIMATION_EASING, fill: 'forwards' }
		);

		this.animations.set( panel, animation );

		// The last frame is held (fill: forwards) until the final state is set, so the panel does not flash.
		animation.addEventListener( 'finish', () => {

			this.animations.delete( panel );

			if ( ! open ) {

				panel.setAttribute( 'hidden', 'until-found' );

			}

			// Back to the natural height, so later content changes (images, resize) are not cut off.
			animation.cancel();

		} );

	}

}

/**
 * Initialize all FAQ blocks on the page.
 */
const init = () => {

	document.querySelectorAll( '.b-faq' ).forEach( ( element ) => new Faq( element ) );

};

if ( document.readyState === 'loading' ) {

	document.addEventListener( 'DOMContentLoaded', init );

} else {

	init();

}
